/**
 * W-2 实名认证。视觉真源 design/kyc-verify.html(左认证表单 / 右「为什么要实名」)。
 * 表单 = 四要素(姓名/身份证号/手机号/银行预留卡号),手填提交。
 *   —— MVP 手填(ADR-0024 D2:真 OCR 延后到 Growth);证件照上传 + OCR 自动识别已移除
 *      (mock-only 无实际价值,证件图待未来微信进件再收)。
 * 提交 → verify → 写会话 store(identityState=KYC_VERIFIED)→ 续写 from / 回个人中心。
 */
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CODE_KYC_RATE_EXCEEDED, verifyKyc } from '@/api/kyc';
import { ServerError } from '@/api/client';
import { useAuthStore, type AuthSession } from '@/store/auth';
import { isKycVerified } from '@/lib/identity';
import { toast } from '@/components/ui/toast';
import type { TokenPairResponse } from '@/api/auth';

const ID_CARD_RE = /^\d{17}[\dXx]$/;
const PHONE_RE = /^1[3-9]\d{9}$/;
const BANK_CARD_RE = /^\d{12,19}$/;

interface FromState {
  from?: string;
}

export function KycVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const identityState = useAuthStore((s) => s.identityState);
  const setSession = useAuthStore((s) => s.setSession);
  const from = (location.state as FromState | null)?.from;
  const alreadyVerified = isKycVerified(identityState);

  const [name, setName] = useState('');
  const [idCardNo, setIdCardNo] = useState('');
  const [phone, setPhone] = useState('');
  const [bankCard, setBankCard] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rateExceeded, setRateExceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitLock = useRef(false);

  const nameOk = name.trim().length > 0;
  const idOk = ID_CARD_RE.test(idCardNo.trim());
  const phoneOk = PHONE_RE.test(phone.trim());
  const bankOk = BANK_CARD_RE.test(bankCard.trim());
  const ready = nameOk && idOk && phoneOk && bankOk;
  const submitDisabled = !ready || submitting || rateExceeded || alreadyVerified;

  const onSubmit = async () => {
    setError(null);
    if (!ready) {
      setError('请完整填写姓名、身份证号、手机号与银行预留卡号');
      return;
    }
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      const token = await verifyKyc({
        name: name.trim(),
        idCardNo: idCardNo.trim(),
        phone: phone.trim(),
        bankCard: bankCard.trim(),
      });
      if (!token.accessToken || !token.accessTokenExpiresIn) {
        throw new Error('实名认证失败,请稍后重试');
      }
      setSession(toAuthSession(token));
      toast.success('实名认证成功');
      navigate(from ?? '/me', { replace: true });
    } catch (err) {
      if (err instanceof ServerError && err.code === CODE_KYC_RATE_EXCEEDED) {
        setRateExceeded(true);
      }
      setError(toMessage(err, '实名认证失败,请稍后重试'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  return (
    <div className="inner section">
      <p className="sm muted mb-6">首页 / 个人中心 / 实名认证</p>
      <div className="row between mb-6">
        <h1 className="h2">实名认证</h1>
        {alreadyVerified ? (
          <span className="badge badge-primary">已实名 ✓</span>
        ) : (
          <span className="badge badge-muted">未实名</span>
        )}
      </div>

      <div className="detail-cols">
        {/* 左:认证表单 */}
        <div className="card card-pad">
          {alreadyVerified && (
            <p className="sm muted mb-4">你已完成实名认证,身份信息一经核验不可修改。</p>
          )}

          <div className="field">
            <label className="label" htmlFor="kyc-name">
              真实姓名
            </label>
            <input
              id="kyc-name"
              className="input"
              type="text"
              placeholder="请输入与证件一致的姓名"
              value={name}
              disabled={alreadyVerified}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="kyc-id">
              身份证号
            </label>
            <input
              id="kyc-id"
              className="input"
              type="text"
              inputMode="text"
              placeholder="请输入 18 位身份证号"
              maxLength={18}
              value={idCardNo}
              disabled={alreadyVerified}
              onChange={(e) => setIdCardNo(e.target.value.replace(/[^\dXx]/g, ''))}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="kyc-phone">
              手机号
            </label>
            <input
              id="kyc-phone"
              className="input"
              type="tel"
              inputMode="numeric"
              placeholder="请输入银行预留手机号"
              maxLength={11}
              value={phone}
              disabled={alreadyVerified}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="kyc-bank">
              银行预留卡号
            </label>
            <input
              id="kyc-bank"
              className="input"
              type="text"
              inputMode="numeric"
              placeholder="请输入本人银行卡号"
              maxLength={19}
              value={bankCard}
              disabled={alreadyVerified}
              onChange={(e) => setBankCard(e.target.value.replace(/\D/g, ''))}
            />
          </div>

          {error && (
            <p className="sm" role="alert" style={{ color: 'var(--color-destructive)' }}>
              {error}
            </p>
          )}

          {!alreadyVerified && (
            <button
              type="button"
              className="btn btn-primary btn-lg btn-block mt-6"
              disabled={submitDisabled}
              onClick={onSubmit}
            >
              {submitting ? '核验中…' : '提交认证'}
            </button>
          )}
          <p className="hint mt-4" style={{ textAlign: 'center' }}>
            认证信息仅用于身份核验,平台严格保密。
          </p>
        </div>

        {/* 右:为什么要实名 */}
        <aside className="sticky-cta">
          <div className="card card-pad">
            <h3 className="h3 mb-4">为什么要实名?</h3>
            <div className="col gap-4">
              <WhyItem icon="🛡️" title="交易更安全" desc="实名后买卖双方身份可信,降低欺诈风险。" />
              <WhyItem icon="💰" title="收益可提现" desc="卖家发布商品与提现收益,均需先完成实名。" />
              <WhyItem icon="✅" title="获得认证标识" desc="实名用户展示认证标识,提升成交转化。" />
            </div>
            <div
              className="card card-pad mt-6"
              style={{ background: 'var(--color-secondary)', boxShadow: 'none' }}
            >
              <p className="sm muted">认证一般即时完成,信息一经核验不可修改,请核对无误后提交。</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function WhyItem({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="row gap-4" style={{ alignItems: 'flex-start' }}>
      <span style={{ fontSize: 'var(--text-xl-size)' }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 'var(--weight-semibold)' }}>{title}</div>
        <p className="sm muted mt-2">{desc}</p>
      </div>
    </div>
  );
}

function toAuthSession(token: TokenPairResponse): AuthSession {
  return {
    accessToken: token.accessToken ?? '',
    refreshToken: token.refreshToken ?? '',
    accessTokenExpiresAt: token.accessTokenExpiresIn ? Date.now() + token.accessTokenExpiresIn * 1000 : 0,
    userId: token.userId ?? '',
    // verify 成功即已实名;后端缺省时兜底 KYC_VERIFIED,避免回跳 /publish 又被 KycGate 拦。
    identityState: token.identityState ?? 'KYC_VERIFIED',
  };
}

function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ServerError) return err.message || fallback;
  return fallback;
}
